import { forwardRef, KeyboardEvent as ReactKeyboardEvent, PropsWithChildren, useCallback } from 'react'

import { css, Global } from '@emotion/react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'
import FocusLock from 'react-focus-lock'

import { Flex } from '../layout/Flex'
import { ArcadeModalActionProvider } from './ArcadeModalActionProvider'
import { ArcadeModalContextProvider } from './ArcadeModalContext'
import ArcadeModalHeader from './ArcadeModalHeader'
import ArcadeModalPaneHeader from './ArcadeModalPaneHeader'

const ModalPaneScrollable = styled.div`
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  background-color: ${({ theme }) => theme.arcadeColors.component.modalBackground};
  position: relative;
  height: calc(100% - 5.5rem);
`

const ModalPaneContent = styled(Flex)`
  padding: ${({ theme }) => `${theme.spacing.defaultContent} ${theme.spacing.l}`};

  ${props => props.theme.breakpoints.tablet} {
    padding: ${({ theme }) => `${theme.spacing.defaultContent} 0`};
  }
`

const FixedPosition = styled(Flex)`
  z-index: 99999;
  position: fixed;
  inset: 0 0 0 0;
  background-color: ${({ theme }) => theme.arcadeColors.component.modalBackground};
  overscroll-behavior: none;
  outline: none;

  &:focus-visible {
    outline: none;
  }
`

const BodyOverFlowHidden = css`
  body {
    overflow: hidden;
  }
`

export type ArcadeModalPaneProps = PropsWithChildren<{
  portalRoot?: string
  ariaLabel: string
  backButtonProps?: {
    onBack: () => void
    ariaLabel: string
  }
  closeButtonProps?: {
    onClose: () => void
    ariaLabel: string
  }
  'data-testid'?: string
}>

/**
 * This component is not exported directly. Use ArcadeModal with variant="fullscreen"
 */
export const ArcadeModalPane = forwardRef<HTMLDivElement, ArcadeModalPaneProps>(
  (
    {
      portalRoot = 'modal-root',
      'data-testid': dataTestId = 'arcade-modal-pane',
      ariaLabel,
      backButtonProps,
      closeButtonProps,
      children,
    },
    ref
  ) => {
    const buttonProps = backButtonProps?.onBack ? { backButtonProps } : { closeButtonProps }

    const handleKeyDown = useCallback(
      (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && closeButtonProps) {
          closeButtonProps.onClose()
        }
      },
      [closeButtonProps]
    )

    const setRefAndFocus = useCallback(
      (element: HTMLDivElement | null) => {
        if (element && !element.contains(document.activeElement)) {
          element.focus()
        }

        if (typeof ref === 'function') {
          ref(element)
        } else if (ref) {
          ref.current = element
        }
      },
      [ref]
    )

    return createPortal(
      <>
        <Global styles={BodyOverFlowHidden} />
        <FocusLock autoFocus={false}>
          <FixedPosition
            flexDirection="column"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            data-testid={dataTestId}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            ref={setRefAndFocus}
          >
            <ArcadeModalContextProvider>
              <ArcadeModalActionProvider>
                <ArcadeModalPaneHeader justifyContent={backButtonProps ? 'start' : 'end'}>
                  {buttonProps && <ArcadeModalHeader {...buttonProps} />}
                </ArcadeModalPaneHeader>
                <ModalPaneScrollable>
                  <ModalPaneContent justifyContent="center">{children}</ModalPaneContent>
                </ModalPaneScrollable>
              </ArcadeModalActionProvider>
            </ArcadeModalContextProvider>
          </FixedPosition>
        </FocusLock>
      </>,
      document.getElementById(portalRoot) as HTMLElement
    )
  }
)

ArcadeModalPane.displayName = 'ArcadeModalPane'
